import { useCallback } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	DeleteContentMutation as DeleteContentMutationData,
	DeleteContentMutationVariables,
} from './__types__/DeleteContentMutation.ts';
import { ContentDeleteActionType } from './__types__/DeleteContentMutation.ts';

/**
 * Returns a callback that deletes the original blank live page created by handleImportCTAClick.
 *
 * When the cfind_5926_upload_in_onboarding experiment is active, the import flow creates a
 * separate parent page as the import destination — bypassing the normal originalDraftId discard
 * mechanism that would otherwise replace the blank page in-place. This hook provides a best-effort
 * cleanup that fires after a successful import in that scenario.
 */
export const useHandleDeleteOnboardingBlankPage = (contentId: string): (() => Promise<void>) => {
	const [deleteContentMutation] = useMutationInterop<
		DeleteContentMutationData,
		DeleteContentMutationVariables
	>(graphql`
		mutation useHandleDeleteOnboardingBlankPageMutation(
			$contentId: ID!
			$action: ContentDeleteActionType!
		) {
			deleteContent(contentId: $contentId, action: $action) {
				contentId
				wasDeleted
			}
		}
	`);

	return useCallback(async () => {
		// Entry points that start an import without an existing draft (e.g. the onboarding
		// CTA in the space side navigation) have no blank page to clean up.
		if (!contentId) {
			return;
		}
		try {
			await deleteContentMutation({
				variables: {
					contentId,
					action: ContentDeleteActionType.DELETE_DRAFT_IF_BLANK,
				},
			});
		} catch (e) {
			// Deletion is best-effort; don't block navigation on failure.
		}
	}, [contentId, deleteContentMutation]);
};
